import { useEffect, useMemo, useState } from "react";
import BaseButton from "Components/Base/BaseButton"; // Ensure this component is correctly imported
import TableContainer from "Components/Base/BaseTable"; // Ensure this component is correctly imported
import { exportPOReport, getVendorList, POReport } from "Api/reposts";
import Loader from "Components/Base/BaseLoader";
import { 
    ORDER_ID, 
    VENDOR_NAME, 
    DELIVERY_DATE, 
    PO_STATUS, 
    PRODUCT_CODE, 
    QUANTITY, 
    RECEIVED_QUANTITY, 
    getPaymentStatusColor,
    po_status,
    END_DATE_LABEL
} from '../../Components/constants/Reports';
import { checkStatusCodeSuccess, deleteUploadedFile, notFound } from "Components/constants/Common";
import { RiSearchLine } from "react-icons/ri";
import BaseInput from "Components/Base/BaseInput";
import { BaseSelect } from "Components/Base/BaseSelect";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { SelectPlaceHolder } from "Components/constants/validation";
import { END_DATE, START_DATE } from "Components/constants/JobCard";
import { BaseImageURL } from "Api/services";
import moment from "moment";
import { PO_ID_LABEL } from "Components/constants/PurchaseOrder";


// Define the types for the purchase order data
interface Product {
    id: number;
    product_code: string;
}

interface PurchaseOrderItem {
    id: number;
    product_quantity: number;
    received_quantity: number;
    receiving_date: string;
    product: Product;
}

interface AuthUser {
    id: number;
    name: string;
}

interface PurchaseOrder {
    id: number;
    auth_id: number;
    delivery_date: string;
    po_status: string;
    authUser: AuthUser;
    purchaseOrderItem: PurchaseOrderItem[];
}

interface Pagination {
    totalItems: number;
    totalPages: number;
    page:number;
    pageSize: number;
}

interface ApiResponse {
    statusCode: number;
    status: string;
    data: {
        orders: PurchaseOrder[];
        pagination: Pagination;
    };
}

const PurchaseOrderReport: React.FC = () => {
    document.title = "Purchase Order Report"; // Set the document title

    const [purchaseOrderData, setPurchaseOrderData] = useState<PurchaseOrder[]>([]); // Initialize purchaseOrderData state
    const [loader, setLoader] = useState<boolean>(false);
    const [error, seterror] = useState<boolean>(false);
    const [customPageSize, setCustomPageSize] = useState<number>(10);
    const[page, setpage] = useState(1);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [columnName, setColumnName] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<string>("asc");

    //state for filtering
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [vendorName, setVendorName] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const handleFetchData = (page: any) => {
        setpage(page);
      };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    
    // Define columns for the table
    const purchaseOrderColumns = useMemo(() => [
        {
            header: PO_ID_LABEL,
            accessorKey: "id",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                return <span>{cell.row.original.po_draft_id || notFound.nullData}</span>;
            },
        },
        {
            header: VENDOR_NAME,
            accessorKey: "authUser.name",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                return <span>{cell.row.original.authUser?.name || notFound.nullData}</span>;
            },
        },
        {
            header: DELIVERY_DATE,
            accessorKey: "delivery_date",
            cell: (cell: any) => {
                const value = cell.row.original.delivery_date;
                return (
                    <div>
                        <span>
                            {value ? moment(value).format("ll") : notFound.nullData}
                        </span>
                    </div>
                );
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: PO_STATUS,
            accessorKey: "po_status",
            cell: (cell: any) => {
                const value = cell.row.original.po_status;
                const backgroundColor = getPaymentStatusColor(value || notFound.nullData);
                return <span className="badge p-1 rounded text-white" style={{ ...backgroundColor }}>{value || notFound.nullData}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
       
        {
            header: QUANTITY,
            accessorKey: "purchaseOrderItem",
            cell: (cell: any) => {
                return <span>
                    
                    {Number(cell.row.original.product_quantity) || notFound.nullData}</span>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            header: RECEIVED_QUANTITY,
            accessorKey: "purchaseOrderItem",
            cell: (cell: any) => {
                return <span>
    
                {Number(cell.row.original.received_quantity) || notFound.nullData}</span>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
    ], []);

    const fetchVendors = async () => {
        try {
            const response = await getVendorList();
            if (response.data && checkStatusCodeSuccess(response.status)) {
                
                setVendorName(response.data);
            } else {
                console.error("Failed to fetch customers:", response);
            }
        } catch (error) {
            console.error("Error fetching customers:", error);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchVendors();
    }, []);
    // Function to fetch purchase order data from API
    const fetchPurchaseOrderData = async () => {
        seterror(false);
        setLoader(true);
         const payload = {
            pageSize: customPageSize,
            page: page,
            "sortValue": "asc",
            "sortKey": "id",
            search: searchValue,
            start_date: startDate,
            end_date: endDate,
            vender_name:selectedVendor,
            po_status:selectedStatus
        };
        POReport(payload)
        .then((response) => {
             if (response.data && checkStatusCodeSuccess(response.status)) {
                    const data = response.data.data;
                    setPurchaseOrderData(data.orders || []);
                    setTotalRecords(data.pagination.totalItems);
                    setTotalPages(data.pagination.totalPages);
                    setCustomPageSize(data.pagination.pageSize);
        
                } else {
                    console.error("Failed to fetch orders:", response);
                    seterror(true);
                }
            })
            .catch((error) => {
                console.error("Error fetching orders:", error);
                seterror(true);
                
            })
            .finally(() => {
                setLoader(false);
            });
    };
    
           
    // Effect to fetch data when page size or page number changes
    useEffect(() => {
        fetchPurchaseOrderData();
    }, [page, customPageSize, columnName, sortOrder, searchValue,startDate,endDate,selectedVendor,selectedStatus]);

    const exportFile = async () => {
        setIsExporting(true);
        const payload = {
            start_date: startDate,
            end_date: endDate,
            vender_name: selectedVendor,
            po_status: selectedStatus,
            // in future need to remove this page size limit
            pageSize: 50 
        };
    
        try {
            const response = await exportPOReport(payload);
            const fileName = response.data.data;
            const fileUrl = `${BaseImageURL}${fileName}`;
            window.open(fileUrl, '_blank');
            
            // Use success message from backend if available
            toast.success(response.data.message || "File exported successfully!");
            if (fileUrl) {
                deleteUploadedFile(fileName)
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                // Use error message from backend if available
                const errorMessage = error.response?.data?.message || "An unexpected error occurred. Please try again later.";
                
                if (error.response?.status === 404) {
                    toast.info(errorMessage);
                } else {
                    toast.error(errorMessage);
                }
            } else {
                toast.error("An unexpected error occurred. Please try again later.");
            }
        } finally {
            setIsExporting(false);  // Stop loader when export ends
        }
    };
    
    const resetFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setSelectedStatus(null);
        setSelectedVendor(null);
        setpage(1); 
    };
    
    return (
        <div className="page-content">
            <div className="flex-grow-1 mx-3">
                <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
                    <span className="fs-4">Purchase Order Report</span>
                    <div className="d-flex align-items-center gap-2">
                    <BaseButton
    className="btn btn-success"
    onClick={exportFile}
    loader={isExporting}  // Pass the loading state here
      // Disable button when exporting
>
    {isExporting ? (
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
    ) : (
        <>
            <i className="mdi mdi-download" /> Export
        </>
    )}
</BaseButton>
                    </div>
                </div>

                <div className="card py-2 px-4 overflow-x-auto">
                <div className="d-flex align-items-center justify-content-end gap-1">
                <div className="container-fluid">
    <div className="row g-3 align-items-end mb-3 justify-content-end">
        {/* Start Date Input */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseInput
                type="date"
                className="form-control"
                label={START_DATE}
                value={startDate || ''}
                onChange={(e) => {
                    setStartDate(e.target.value || null);
                    setpage(1);
                }}
            />
        </div>

        {/* End Date Input */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseInput
                type="date"
                className="form-control"
                label={END_DATE_LABEL}
                value={endDate || ''}
                onChange={(e) => {
                    setEndDate(e.target.value || null);
                    setpage(1);
                }}
            />
        </div>

        {/* Vendor Name Select */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseSelect
                placeholder={SelectPlaceHolder("")}
                label={VENDOR_NAME}
                options={vendorName.map((vendor: any) => ({
                    label: vendor.name,
                    value: vendor.name,
                }))}
                value={selectedVendor}
                handleChange={(name: any, value: any) => {
                    setSelectedVendor(value);
                    setpage(1);
                }}
            />
        </div>

        {/* PO Status Select */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseSelect
                placeholder={SelectPlaceHolder("")}
                label={PO_STATUS}
                options={po_status}
                value={selectedStatus}
                handleChange={(name: any, value: any) => {
                    setSelectedStatus(value === "" ? null : value);
                    setpage(1);
                }}
                
                
                className="text-nowrap"
            />
        </div>

        {/* Reset Button */}
        <div className="col-12 col-sm-6 col-lg-auto ms-lg-2 d-flex justify-content-end" style={{ minWidth: '100px' }}>
            <BaseButton
                className="btn btn-danger px-4"
                onClick={resetFilters}
                
            >
                <i className="mdi mdi-refresh me-1" />
                Reset
            </BaseButton>
        </div>
    </div>
</div>
</div>
                    {(!error && (loader || purchaseOrderData.length > 0))?(
                      
                     
                        <TableContainer
                            customPageSize={customPageSize}
                            setCustomPageSize={setCustomPageSize}
                            isGlobalFilter={true}
                            columns={purchaseOrderColumns}
                            data={purchaseOrderData}
                            tableClass="table bg-white"
                            manualPagination={true}
                            manualFiltering={false}
                            tableLoader={loader}
                            hasManualPagination={true}
                            SearchPlaceholder="Search purchase orders..."
                            currentPage={page}
                            setCurrentPage={setpage}
                            totalPages={totalPages}
                            totalRecords={totalRecords} 
                            totalNumberOfRows={totalRecords} 
                            fetchData={handleFetchData} 
                            fetchSortingData={fetchPurchaseOrderData} 
                            
                        />
                    ) : (
                        <div className="py-4 text-center">
                        <div>
                          <span className="fs-1 text-success">
                            <RiSearchLine />
                          </span>
                        </div>
                        <div className="mt-4">
                          <h5>PO Data Not Found</h5>
                          <p className="text-muted">The data you are looking for not available.</p>
                        </div>
                      </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PurchaseOrderReport;
